"use client";

import React, {
  ChangeEvent,
  useCallback,
  useState,
  FC,
  useMemo,
  MouseEvent,
  useEffect,
  useContext,
} from "react";

import { useIsAuthenticated } from "@azure/msal-react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import clsx from "clsx";
import { omit } from "lodash";
import { useParams, usePathname } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { ShoppingTypeFilterProps } from "~/bff/components/ShoppingTypeFilter";
import { BreadcrumbsItem } from "~/bff/types/BreadcrumbsItem";
import { ShoppingTypeFilterFilterdOptions } from "~/bff/types/ShoppingTypeFilterFilterdOptions";
import { Store } from "~/bff/types/Store";
import { DropdownControl } from "~/components/dropdown-control/component";
import { ProductTypes } from "~/components/plp-page/constants";
import { StoreSelector } from "~/components/store-selector/component";
import { StoreSelectorUseType } from "~/components/store-selector/types";
import { Tag } from "~/components/tag/component";
import { TagType } from "~/components/tag/types";
import {
  DataLayerData,
  DataLayerDataTypes,
  DataLayerFilterType,
  EventTypes,
  EventsNames,
} from "~/constants/data-layer";
import { Routes } from "~/constants/request";
import { AuthContext } from "~/context/auth/context";
import { DictionaryLabelContext } from "~/context/dictionary-label/context";
import { DictionaryKeys } from "~/context/dictionary-label/types";
import { StoreSelectorContext } from "~/context/store-selector/context";
import { getCountryCode } from "~/helpers/country/get-country-code";
import { getPageCategoriesTitles } from "~/helpers/get-page-categories-titles";
import { pushDataLayer } from "~/helpers/push-data-layer";
import { getSelectedOrPreferredStoreId } from "~/helpers/selected-store/get-selected-or-preferred-store-id";
import { isCurrentStoreInCurrentLocation } from "~/helpers/selected-store/is-current-store-in-current-location";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { useClickAndCollectTrialStore } from "~/hooks/use-click-and-collect-trial-store";
import { usePdpStockAvailsCampaign } from "~/hooks/use-pdp-stock-avails-campaign";
import { useTouchDevice } from "~/hooks/use-touch-device/index";
import { useAzureConfigurator } from "~/services/azure-configurator/use-azure-configurator";
import { NotAvailable24Variation } from "~/theme/icons/components/not-available-variation";
import { NotRanged24 } from "~/theme/icons/components/not-ranged";
import { Nullable } from "~/types/general.types";
import { ProductRequestType } from "~/types/products-request-handler";

import { SHOP_FILTER_CLASSES } from "../filter-shared.constants";
import {
  ShopFilterContainer,
  ShopFilterTooltipStyled,
} from "../filter-shared.styled";

import { getKeyByValue } from "./helpers/get-key-by-value";

export interface ShopFilterProps extends ShoppingTypeFilterProps {
  className?: string;
  isShopFilter: boolean | null;
  currentStore: Nullable<Store>;
  onUpdateStore: (store: Nullable<Store>) => void;
  onClick: (store: Nullable<Store>) => void;
  setIsShopFilterChipOptionShow: React.Dispatch<React.SetStateAction<boolean>>;
  shopFilterSortingParam?: string;
  setShopFilterSortingParam?: (value: ProductRequestType) => void;
  onShopFilterDropdown?: (value: ProductRequestType) => void;
  isShopFilterChipToClose?: boolean;
  shoppingTypeDropdown?: Nullable<boolean>;
  breadcrumbs?: Nullable<Nullable<BreadcrumbsItem>[]>;
  numFound?: Nullable<number>;
  sortOrderForGA?: string;
}

const CITY_PLACEHOLDER = "{{city}}";

export const ShopFilter: FC<ShopFilterProps> = ({
  className,
  isShopFilter,
  currentStore,
  onUpdateStore,
  onClick,
  onShopFilterDropdown,
  editLinkName,
  filterByCheckboxTitle,
  noClickPlusCollectTrialNotification,
  filterOptions,
  shopInCheckboxTitle,
  clickCollectTooltip,
  shopFilterSortingParam,
  setShopFilterSortingParam,
  setIsShopFilterChipOptionShow,
  isShopFilterChipToClose,
  shoppingTypeDropdown = false,
  breadcrumbs,
  sortOrderForGA,
  numFound,
}) => {
  const { locale } = useParams<GlobalPathParams>();
  const config = useAzureConfigurator(locale);
  const isTouchDevice = useTouchDevice();
  const dictionary = useContext(DictionaryLabelContext);
  const storeSelector = useContext(StoreSelectorContext);
  const authContext = useContext(AuthContext);
  const { shouldDisplayCCTag } = useClickAndCollectTrialStore(currentStore);
  const { showNewIcon } = usePdpStockAvailsCampaign();

  const prepareShopInCheckboxTitle = useMemo(() => {
    let storeName = currentStore?.geomodifier
      ? currentStore?.geomodifier
      : currentStore?.address?.city;

    if (
      config?.featureFlags?.displayRegion?.enabled &&
      currentStore?.address?.region
    ) {
      storeName = [storeName, currentStore?.address?.region].join(", ");
    }

    return shopInCheckboxTitle?.replace(CITY_PLACEHOLDER, storeName || "");
  }, [currentStore, shopInCheckboxTitle, config?.featureFlags]);

  const pathname = usePathname();
  const countryCode = locale.split("-")[1];
  const isRbpPage = pathname === Routes.RBP;
  const isAuthenticated = useIsAuthenticated();

  const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(false);
  const [storesInStoreSelector, setStoresInStoreSelector] = useState<
    Nullable<Store>[]
  >([]);

  const [selectedProductType, setSelectedProductType] = useState<
    Nullable<string> | undefined
  >(
    filterOptions?.[
      shopFilterSortingParam as keyof ShoppingTypeFilterFilterdOptions
    ] || filterOptions?.allProducts,
  );

  const isClickAndCollectStore = useMemo(() => {
    return currentStore?.clickAndCollectStore;
  }, [currentStore?.clickAndCollectStore]);

  const isClickAndCollectEnabled = useMemo(
    () => config?.featureFlags?.clickAndCollect?.enabled,
    [config],
  );

  const isShopFilterAndCurrentStore = useMemo(
    () => isShopFilter && isCurrentStoreInCurrentLocation(countryCode, currentStore),
    [isShopFilter, currentStore, countryCode],
  );

  const listOfDropdownValues = Object.values(omit(filterOptions, ["__typename"]));

  const onStoresChange = useCallback((stores?: Nullable<Store>[]) => {
    setStoresInStoreSelector(stores ?? []);
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (isShopFilterChipToClose) {
        setIsShopFilterChipOptionShow((prev) => !prev);
      }
      setSelectedProductType(event.target.value as ProductTypes);

      if (filterOptions) {
        setShopFilterSortingParam &&
          setShopFilterSortingParam(
            getKeyByValue(filterOptions, event?.target?.value),
          );
        onShopFilterDropdown &&
          onShopFilterDropdown(getKeyByValue(filterOptions, event.target.value));
      }
    },
    [
      filterOptions,
      isShopFilterChipToClose,
      onShopFilterDropdown,
      setIsShopFilterChipOptionShow,
      setShopFilterSortingParam,
    ],
  );

  const handleChangeCurrentStore = useCallback(
    (store?: Nullable<Store>) => {
      store && onUpdateStore(store);
      if (store && !isShopFilter) {
        onClick(store);
      }
    },
    [isShopFilter, onClick, onUpdateStore],
  );

  const [offset, setOffset] = useState(0);
  const onOffsetChange = useCallback((offset: number) => setOffset(offset), []);

  const [prevGaDataAsString, setPrevGaDataAsString] = useState<string | null>(null);

  const getDataForGA: () => DataLayerDataTypes = useCallback(() => {
    const country = getCountryCode(locale);
    const { pageCategory, pageSubCategory, pageSubSubCategory } =
      getPageCategoriesTitles(breadcrumbs, pathname);

    return {
      [DataLayerData.event]: EventsNames.plpApplyFilter,
      [DataLayerData.siteCountry]: country,
      [DataLayerData.type]: EventTypes.applyFilter,
      [DataLayerData.pageCategory]: pageCategory,
      [DataLayerData.pageSubCategory]: pageSubCategory,
      [DataLayerData.pageSubSubCategory]: pageSubSubCategory,
      [DataLayerData.loggedInStatus]: isAuthenticated,
      [DataLayerData.storeSelected]: currentStore?.geomodifier,
      [DataLayerData.filterType]: DataLayerFilterType.filterByStore,
      [DataLayerData.filterValue]: currentStore?.geomodifier,
      [DataLayerData.filterResults]: numFound,
      [DataLayerData.sortOrder]: sortOrderForGA,
    };
  }, [
    locale,
    breadcrumbs,
    pathname,
    isAuthenticated,
    currentStore?.geomodifier,
    numFound,
    sortOrderForGA,
  ]);

  useEffect(() => {
    const dataForGA = getDataForGA();
    const currGaDataAsString = JSON.stringify(dataForGA);

    if (isShopFilterAndCurrentStore && currGaDataAsString !== prevGaDataAsString) {
      pushDataLayer(dataForGA);
      setPrevGaDataAsString(JSON.stringify(dataForGA));
    }
  }, [getDataForGA, isShopFilterAndCurrentStore, prevGaDataAsString]);

  const handleCheckboxOpenStoreSelector = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, callback: () => void) => {
      setIsTooltipVisible(false);
      if (
        getSelectedOrPreferredStoreId(authContext?.account) &&
        isCurrentStoreInCurrentLocation(countryCode, currentStore)
      ) {
        if (filterOptions && selectedProductType) {
          setShopFilterSortingParam?.(
            getKeyByValue(filterOptions, selectedProductType),
          );
        }
        onClick(currentStore);
        return;
      }
      callback();
    },
    [
      filterOptions,
      onClick,
      selectedProductType,
      setShopFilterSortingParam,
      authContext?.account,
      currentStore,
      countryCode,
    ],
  );

  const handleTooltipState = useCallback(
    (tooltipState: boolean) => () => {
      setIsTooltipVisible(tooltipState);
    },
    [],
  );

  const handleTooltipClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (isTouchDevice) {
        event.stopPropagation();
        event.preventDefault();
        setIsTooltipVisible((previousIsTooltipVisible) => {
          if (previousIsTooltipVisible) {
            event.currentTarget?.blur();
          }
          return !previousIsTooltipVisible;
        });
      }
    },
    [isTouchDevice],
  );

  const toShowDropDown: Nullable<boolean> = useMemo(() => {
    return isRbpPage ? shoppingTypeDropdown : true;
  }, [isRbpPage, shoppingTypeDropdown]);

  useEffect(() => {
    const productType =
      filterOptions?.[
        shopFilterSortingParam as keyof ShoppingTypeFilterFilterdOptions
      ];

    if (productType && selectedProductType !== productType) {
      setSelectedProductType(productType);
    }
  }, [filterOptions, selectedProductType, shopFilterSortingParam]);

  const shouldDisplayNoCCNotification = useMemo(() => {
    return (
      isClickAndCollectEnabled && (!isClickAndCollectStore || !shouldDisplayCCTag)
    );
  }, [isClickAndCollectEnabled, isClickAndCollectStore, shouldDisplayCCTag]);

  const breadcrumbsFiltered = useMemo(
    () =>
      Array.isArray(breadcrumbs)
        ? breadcrumbs.filter<BreadcrumbsItem>(
            (el): el is BreadcrumbsItem => el !== undefined && el !== null,
          )
        : [],
    [breadcrumbs],
  );

  return (
    // This should be fixed
    <StoreSelector
      onCurrentStoreChange={handleChangeCurrentStore}
      onStoresChange={onStoresChange}
      onOffsetChange={onOffsetChange}
      storesInStoreSelector={storesInStoreSelector}
      currentStore={currentStore}
      offset={offset}
      type={StoreSelectorUseType.filter}
      mainProps={storeSelector?.props}
      breadcrumbs={breadcrumbsFiltered}
    >
      {({ openStoreSelector }: { openStoreSelector: () => void }) => {
        return (
          <ShopFilterContainer
            id="shop-filter"
            className={className}
            showNewIcon={showNewIcon}
            {...getTestAutomationProps("shop-filter")}
          >
            <Box className={SHOP_FILTER_CLASSES.filterItemShop}>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(event) =>
                      handleCheckboxOpenStoreSelector(event, openStoreSelector)
                    }
                    className={SHOP_FILTER_CLASSES.shopFilterCheckbox}
                    checked={!!isShopFilterAndCurrentStore}
                    {...getTestAutomationProps("checkbox")}
                  />
                }
                label={
                  <>
                    {isShopFilterAndCurrentStore ? (
                      <Box>
                        <Box>
                          <Typography
                            className={SHOP_FILTER_CLASSES.storeAddressCity}
                            variant="body2"
                          >
                            {prepareShopInCheckboxTitle}
                          </Typography>
                          <Typography
                            variant="body2"
                            className={SHOP_FILTER_CLASSES.storeAddress}
                          >
                            {currentStore?.address.line1}
                          </Typography>
                        </Box>
                        {shouldDisplayCCTag && (
                          <Box className={SHOP_FILTER_CLASSES.productFilter}>
                            <ShopFilterTooltipStyled
                              title={clickCollectTooltip?.clickCollectStore}
                              open={isTooltipVisible}
                              onClose={handleTooltipState(false)}
                              onOpen={handleTooltipState(true)}
                              placement="top-start"
                              disableTouchListener={isTouchDevice}
                              arrow
                              slotProps={{
                                tooltip: {
                                  className: SHOP_FILTER_CLASSES.tooltipIsCC,
                                },
                              }}
                            >
                              <Box onClick={handleTooltipClick}>
                                <Tag
                                  className={SHOP_FILTER_CLASSES.productFilterTag}
                                  type={TagType.clickAndCollectTrialStore}
                                  title={
                                    dictionary
                                      ? dictionary[
                                          DictionaryKeys.ClickPlusCollectTrialStore
                                        ]
                                      : ""
                                  }
                                />
                              </Box>
                            </ShopFilterTooltipStyled>

                            {toShowDropDown && (
                              <DropdownControl
                                onChange={handleChange}
                                value={selectedProductType}
                                className={SHOP_FILTER_CLASSES.productFilterSelect}
                                {...getTestAutomationProps("dropdown")}
                              >
                                {listOfDropdownValues.map((productType) => (
                                  <MenuItem
                                    key={productType || ""}
                                    value={productType || ""}
                                  >
                                    {productType}
                                  </MenuItem>
                                ))}
                              </DropdownControl>
                            )}
                          </Box>
                        )}
                        {shouldDisplayNoCCNotification && (
                          <ShopFilterTooltipStyled
                            title={clickCollectTooltip?.notClickCollectStore}
                            placement="top-start"
                            open={isTooltipVisible}
                            onClose={handleTooltipState(false)}
                            onOpen={handleTooltipState(true)}
                            disableTouchListener={isTouchDevice}
                            arrow
                            slotProps={{
                              tooltip: {
                                className: SHOP_FILTER_CLASSES.tooltipNotCC,
                              },
                            }}
                          >
                            <Box
                              className={
                                SHOP_FILTER_CLASSES.productWithoutClickAndCollect
                              }
                              onClick={handleTooltipClick}
                            >
                              {showNewIcon ? (
                                <NotAvailable24Variation />
                              ) : (
                                <NotRanged24 />
                              )}
                              <Typography
                                noWrap
                                variant="body2"
                                className={
                                  SHOP_FILTER_CLASSES.productWithoutClickAndCollectText
                                }
                              >
                                {noClickPlusCollectTrialNotification}
                              </Typography>
                            </Box>
                          </ShopFilterTooltipStyled>
                        )}
                      </Box>
                    ) : (
                      <Typography noWrap variant="body2">
                        {filterByCheckboxTitle}
                      </Typography>
                    )}
                  </>
                }
              />
              {isShopFilterAndCurrentStore && (
                <Button
                  className={clsx(
                    SHOP_FILTER_CLASSES.editButton,
                    "body2",
                    "underlined",
                  )}
                  variant="text"
                  onClick={openStoreSelector}
                >
                  {editLinkName}
                </Button>
              )}
            </Box>
          </ShopFilterContainer>
        );
      }}
    </StoreSelector>
  );
};
